import { DeepPartial } from 'redux'
import { useCallback, useEffect, useState } from 'react'
import {
  addDays,
  differenceInDays,
  differenceInMilliseconds,
  format,
  getISODay,
  intervalToDuration,
  isBefore,
  minutesToMilliseconds,
  parse,
  sub,
  subDays,
} from 'date-fns'
import { Button } from '@pinchenterprisesnpm/friday-ui'
import { useSelector } from 'react-redux'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import {
  SHIFT_DATE_FORMAT,
  TIME_FORMAT_STRING,
} from '@src/helpers/timeAndDateHelpers/constants'
import { IShift, SHIFT_STATUS, SHIFT_TYPE } from '@network/models/shift.model'
import {
  FEATURE_TOGGLE_MULTIDAY_REVAMP,
  FEATURE_TOGGLE_INVITE_UPSHIFTERS_REVAMP,
  FEATURE_TOGGLE_RESTRICT_LAST_MINUTE_CANDIDATE_ADDITIONS_IN_THE_VMS,
} from 'config/featureToggles'
import { minutesDifferenceBetweenTwoDates } from 'helpers/helperFunctions'
import { TGenericModalConfig } from 'helpers/GenericModal/GenericModalProvider'
import { calculateShiftDurationHours } from 'components/business/Shift/common/checkers/shiftLengthChecker'
import { selectProperties } from 'data/business/Properties/selectors'
import { hasBusinessPayRateUpdateRestriction } from 'helpers/businessEntityHelpers'
import AuthService from '../authService'
import {
  TDay,
  IModalEditShiftValues,
  TWeeklyType,
  TSingleDayShift,
  IModalPostFromTemplateShiftValues,
  TEditDuplicateShiftValues,
  IModalDuplicateShiftValues,
} from './types'

export const SHIFT_MAX_HOURLY_WAGE = 35

export const howManyDirtyDays = (days: TDay[], dayOfWeek: number) => {
  let dirtyCount = 0

  days.forEach(day => {
    // Convert day_start to Date if it's not already
    const dayStartDate =
      day.day_start instanceof Date ? day.day_start : new Date(day.day_start)
    const isRequestedDay = getISODay(dayStartDate) === dayOfWeek + 1
    if (day.is_dirty && isRequestedDay) dirtyCount++
  })

  return dirtyCount
}

export const dateIdMap = (days: TDay[]) => {
  const map = days.reduce(
    (current_map, current_day) => ({
      ...current_map,
      [current_day.date_start]: current_day,
    }),
    {},
  )

  return map
}

// TODO remove
export const extractFirstAndLastDate = (days: any[]) => {
  return days.length > 0
    ? [days[0].date_start, days[days.length - 1].date_start]
    : []
}

/**
 * Updates days in an array with new info for every day
 *
 * @param daysArray Multiday days or Individual days array from Formik
 * @param customizer
 * @param enforceUpdate Update all days not taking into account whether they were edited or not
 */
export const updateDays = <T extends Partial<TDay> = {}>(
  daysArray: TDay[],
  customizer: ((day: TDay, index: number) => T) | T,
  dirtyUpdate?: boolean,
  enforceUpdate?: boolean,
) => {
  let newDaysArray = [] as TDay[]

  if (dirtyUpdate) {
    newDaysArray = daysArray.map((day, index) => {
      if (Number(day.punch_cards_count) > 0) return day
      return {
        ...day,
        ...(!day.is_dirty || enforceUpdate
          ? typeof customizer === 'function'
            ? customizer(day, index)
            : customizer
          : undefined),
      }
    })
  } else {
    newDaysArray = daysArray.map((day, index) => {
      if (Number(day.punch_cards_count) > 0) return day
      return {
        ...day,
        ...(typeof customizer === 'function'
          ? customizer(day, index)
          : customizer),
      }
    })
  }

  return newDaysArray
}

/**
 * Generates day objects between two dates for multiday shifts that are not to hire
 *
 * @param startDay Start day of the range
 * @param endDay End day of the range
 */
export const generateMultidayDays = (
  startDay: Date | undefined,
  endDay: Date | undefined,
  values: TEditDuplicateShiftValues,
) => {
  if (!startDay || !endDay) {
    return []
  }

  const numberOfDays = differenceInDays(endDay, startDay)
  const days = []

  /**
   * We generate the days in the inclusive range [startDay, endDay]
   */
  for (let i = 0; i <= numberOfDays; i++) {
    const currentDayStart = addDays(startDay, i)
    const currentDayEnd = getFinalDay(
      currentDayStart,
      values.hour_start,
      values.hour_end,
    )

    const date_start = format(currentDayStart, SHIFT_DATE_FORMAT)
    const date_end = format(currentDayEnd, SHIFT_DATE_FORMAT)

    const { date_ids } = values as IModalEditShiftValues

    let id = i
    let working = true
    let punch_cards_count = 0
    let existing = false

    if (date_ids[date_start]) {
      id = date_ids[date_start].id
      working = date_ids[date_start].working
      punch_cards_count = date_ids[date_start].punch_cards_count
      existing = true
    }

    const day: TDay = {
      id,
      day_start: currentDayStart,
      day_end: currentDayEnd,
      date_start,
      date_end,
      hour_start: values.hour_start,
      hour_end: values.hour_end,
      break: values.break,
      pay_value: values.pay_value,
      working,
      punch_cards_count,
      existing,
      starting_day: false,
    }

    days.push(day)
  }

  return days
}

/**
 * Generates individual days array
 *
 * @param values Values from Formik
 * @param partialValues Partial object from Values
 */
export const generateIndividualDays = <
  T extends DeepPartial<TEditDuplicateShiftValues> = {}
>(
  values: TEditDuplicateShiftValues,
  partialValues: T,
) => {
  const {
    weekly: v_weekly,
    number_of_weeks: v_number_of_weeks,
    individual_day_start: v_individual_day_start,
  } = values
  const {
    weekly: pv_weekly,
    number_of_weeks: pv_number_of_weeks,
    individual_day_start: pv_individual_day_start,
  } = partialValues

  const weekly = { ...v_weekly, ...pv_weekly }
  const number_of_weeks =
    pv_number_of_weeks !== 0
      ? pv_number_of_weeks || v_number_of_weeks
      : pv_number_of_weeks
  const individual_day_start = (pv_individual_day_start ||
    v_individual_day_start) as Date

  const startDay =
    individual_day_start instanceof Date
      ? individual_day_start
      : new Date(individual_day_start)
  const endDay = addDays(startDay, number_of_weeks * 7)

  const numberOfDays = differenceInDays(endDay, startDay)
  const startDayIndex = getISODay(startDay)

  const days = []

  const { date_ids } = values as IModalEditShiftValues

  /**
   * We generate the days in the inclusive range [startDay, endDay]
   */
  for (let i = 0; i < numberOfDays; i++) {
    const { date_ids } = values as IModalEditShiftValues

    const currentDayStart = addDays(startDay, i)
    const currentDayEnd = getFinalDay(
      currentDayStart,
      values.hour_start,
      values.hour_end,
    )

    const date_start = format(currentDayStart, SHIFT_DATE_FORMAT)
    const date_end = format(currentDayEnd, SHIFT_DATE_FORMAT)

    let id = i
    const working = weekly[((startDayIndex - 1 + (i % 7)) % 7) as TWeeklyType]
    let punch_cards_count = 0
    let existing = false
    let is_dirty = false

    if (date_ids[date_start]) {
      id = date_ids[date_start].id
      punch_cards_count = date_ids[date_start].punch_cards_count
      existing = true
      is_dirty = true
    }

    const day: TDay = {
      id,
      day_start: currentDayStart,
      day_end: currentDayEnd,
      date_start,
      date_end,
      hour_start: values.hour_start,
      hour_end: values.hour_end,
      break: values.break,
      pay_value: values.pay_value,
      working,
      punch_cards_count,
      existing,
      is_dirty,
      starting_day: false,
    }

    days.push(day)
  }

  return days
}

const extractPeriod = (time: string) => {
  if (time.includes('AM')) {
    return 'AM'
  } else if (time.includes('PM')) {
    return 'PM'
  } else return null
}

const extractHourAndMinute = (time: string) => {
  const [hour, remainder] = time.split(':')
  const other = remainder || ''
  const [minute, period] = other.split(' ')

  return [hour, minute]
}

const isStartBeforeEnd = (time_start: string, time_end: string) => {
  const [hour_start, minute_start] = extractHourAndMinute(time_start)
  const [hour_end, minute_end] = extractHourAndMinute(time_end)

  if (Number(hour_start) === 12) {
    // We know that times have the same period
    return true
  }
  if (Number(hour_start) > Number(hour_end)) return false
  if (Number(hour_start) === Number(hour_end)) {
    if (Number(minute_start) > Number(minute_end)) return false
    else return true
  }

  return true
}

/**
 * Helper function to check if two times are in the same day
 * @param time_start Start time
 * @param time_end End time
 * @returns True if the times are in the same day, false otherwise
 */
export const areTimesInTheSameDay = (time_start: string, time_end: string) => {
  const referenceDate = new Date()
  const startDate = parse(time_start, TIME_FORMAT_STRING.hh_mm_a, referenceDate)
  const endDate = parse(time_end, TIME_FORMAT_STRING.hh_mm_a, referenceDate)

  // If end time is before or equal to start time, the shift crosses midnight
  return endDate > startDate
}

/**
 * Helper function to get the final day of a shift
 * @param day_start Start day
 * @param hour_start Start hour
 * @param hour_end End hour
 * @returns The final day of the shift
 */
export const getFinalDay = (
  day_start: Date,
  hour_start: string,
  hour_end: string,
) => {
  return areTimesInTheSameDay(hour_start, hour_end)
    ? new Date(day_start)
    : addDays(new Date(day_start), 1)
}

export const correctEndDate = (
  day_end: Date,
  hour_start: string,
  hour_end: string,
): string => {
  const dayEndDate = day_end instanceof Date ? day_end : new Date(day_end)
  return areTimesInTheSameDay(hour_start, hour_end)
    ? format(dayEndDate, SHIFT_DATE_FORMAT)
    : format(subDays(dayEndDate, 1), SHIFT_DATE_FORMAT)
}

/**
 *
 * @param {*} dateStart
 * @param {*} hourStart
 * @param {*} hourEnd
 */
export const calculateEndDateFomStartDateAndHours = (
  dateStart: string,
  hourStart: string,
  hourEnd: string,
) => {
  let dateEnd = dateStart
  const beginningTime = parse(hourStart, 'hh:mm a', new Date())
  const endTime = parse(hourEnd, 'hh:mm a', new Date())
  if (isBefore(endTime, beginningTime)) {
    dateEnd = format(addDays(new Date(dateStart), 1), 'MM/dd/yyyy')
  }

  return dateEnd
}

interface ICalculateShiftAmountProps {
  startTime: string
  endTime: string
  rate: string
  unpaidBreak: number
}

interface ICalculateUpshiftFeeProps {
  commissionPercent: number
  totalShiftAmount: number
}

interface ICalculateShiftTotalProps {
  totalShiftAmount: number
  upshiftFee: number
}

export const parseCalculation = (value: number) => {
  return parseFloat(value.toString()).toFixed(2)
}

export const calculateShiftAmount = ({
  startTime,
  endTime,
  rate,
  unpaidBreak,
}: ICalculateShiftAmountProps) => {
  // We're initializing the date as today's date since it doesn't make a difference in the calculations
  // it's only used to see if the shift will end in a new day (after 12AM)
  const dateStart = format(new Date(), 'MM/dd/yyyy')
  // Calculate the end date of the shift
  const dateEnd = calculateEndDateFomStartDateAndHours(
    dateStart,
    startTime,
    endTime,
  )
  // Parse the end date time to a hh:mm a format
  const timeDateEnd = parse(endTime, 'hh:mm a', new Date(dateEnd))
  // Parse the start date time to a hh:mm a format
  const timeDateStart = parse(startTime, 'hh:mm a', new Date(dateStart))
  // Convert the break from minutes to milliseconds
  const breakInMilliseconds = minutesToMilliseconds(unpaidBreak)
  // Convert the break into a duration object
  const breakDuration = intervalToDuration({
    start: 0,
    end: breakInMilliseconds,
  })
  // Create a duration object for the shift
  const shiftDuration = intervalToDuration({
    start: 0,
    end: sub(
      differenceInMilliseconds(timeDateEnd, timeDateStart),
      breakDuration,
    ),
  })
  // Get the hours from the shift duration object
  const hourDifference = shiftDuration.hours || 0
  // Divide the minute difference from the duration by an hour (60 minutes)
  const minutesDifference = (shiftDuration.minutes || 0) / 60
  // Add the hour and minutes difference in a float variable
  const timeDifference = hourDifference + minutesDifference

  // Multiply the shift duration by the pay rate
  const totalShiftAmount = timeDifference * Number(rate)

  return totalShiftAmount
}

export const calculateUpshiftFee = ({
  commissionPercent,
  totalShiftAmount,
}: ICalculateUpshiftFeeProps) => {
  const upshiftFee = totalShiftAmount * commissionPercent

  return upshiftFee
}

export const calculateShiftTotal = ({
  totalShiftAmount,
  upshiftFee,
}: ICalculateShiftTotalProps) => {
  const shiftTotal = totalShiftAmount + upshiftFee
  return shiftTotal
}

/**
 *
 * @param day
 */
// TODO add stats.punchcards_count so we can disable day editing if the particular day has at least one punchcard
const dayMapper = (
  weekly: { [key in TWeeklyType]: boolean },
  propertyMinWage?: string,
) => (day: any) => {
  const {
    id,
    date_start,
    date_end,
    hour_start,
    hour_end,
    pay_value,
    break: unpaid_break,
    working_day: working,
    punch_cards_count,
    starting_day,
  } = day

  const dateStartParsed = parse(date_start, SHIFT_DATE_FORMAT, new Date())
  const dateEndParsed = parse(date_end, SHIFT_DATE_FORMAT, new Date())
  const dayIndex = (getISODay(dateStartParsed) - 1) as TWeeklyType
  const isDayDirty = weekly[dayIndex] !== working

  const mappedDay: TDay = {
    id,
    day_start: dateStartParsed,
    day_end: dateEndParsed,
    date_start,
    date_end,
    hour_start,
    hour_end,
    pay_value:
      propertyMinWage &&
      shouldUpdatePayValueBasedOnMinWage(pay_value, propertyMinWage)
        ? propertyMinWage
        : pay_value,
    break: unpaid_break,
    working,
    punch_cards_count,
    existing: true,
    is_dirty: isDayDirty,
    starting_day,
  }

  return mappedDay
}

export const mapDays = (
  days: any[],
  weekly: { [key in TWeeklyType]: boolean },
  propertyMinWage?: string,
) => {
  return days.map(dayMapper(weekly, propertyMinWage))
}

export const mapWeeklyOn = (weekly: any) => {
  return (weekly || []).reduce(
    (currentObject: any, currentDay: any) => ({
      ...currentObject,
      [currentDay - 1]: true,
    }),
    {
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
      5: false,
      6: false,
    },
  )
}

export const breakDropdownData = [
  {
    value: 0,
    label: 'No Break',
  },
  {
    value: 30,
    label: '30 min',
  },
  {
    value: 45,
    label: '45 min',
  },
  {
    value: 60,
    label: '60 min',
  },
  {
    value: 75,
    label: '75 min',
  },
]

/**
 *
 * Returns the dropdown values ready for Select
 * @param {Boolean} dropdownReady
 */
export const getBreakValue = (dropdownReady = false) => {
  const params = [
    {
      value: 0,
      label: 'No Break',
      action: 'No Break',
    },
    {
      value: 30,
      label: '30 min',
      action: 30,
    },
    {
      value: 45,
      label: '45 min',
      action: 45,
    },
    {
      value: 60,
      label: '60 min',
      action: 60,
    },
    {
      value: 75,
      label: '75 min',
      action: 75,
    },
  ]
  return params
}

/**
 * If the business entity has disabled selective claim mode for him, reset the fields before posting the shift
 * (fields may be lingering from the shift we are duplicating).
 * @param shift Can be an array of single day shift(s) or an object
 */
export const handleSelectiveClaimFields = (shift: any) => {
  const businessEntity = AuthService.getBusinessEntity()

  let resetScmFields = {}
  const shouldResetAAByCertificatesFields =
    !businessEntity!.auto_accept_by_certificates ||
    (businessEntity?.auto_accept_by_certificates &&
      businessEntity.business_setting?.hide_auto_accept_certificates)

  if (shouldResetAAByCertificatesFields) {
    resetScmFields = {
      ...resetScmFields,
      auto_accept_by_certificates: false,
      auto_accept_certificate_ids: [],
    }
  }

  if (!businessEntity!.auto_accept_by_favored) {
    resetScmFields = {
      ...resetScmFields,
      auto_accept_by_favored: false,
    }
  }

  if (!businessEntity!.auto_accept_by_worked_before) {
    resetScmFields = {
      ...resetScmFields,
      auto_accept_by_worked_before: false,
    }
  }

  if (Array.isArray(shift)) {
    const newShifts = shift.map(singleShift => {
      return {
        ...singleShift,
        ...resetScmFields,
      }
    })
    return newShifts
  } else {
    const newShift = {
      ...shift,
      ...resetScmFields,
    }
    return newShift
  }

  return shift
}

/**
 * Updates all of the Single day shifts only one field at a time.
 * @param shifts
 * @param field
 * @param value
 * @returns Single day shifts
 */
export const updateSingleDayShifts = (
  shifts: TSingleDayShift[],
  field: string,
  value: string | number,
) => {
  if (!shifts) return []

  return shifts.map(shift => ({
    ...shift,
    [field]: value,
  }))
}

/**
 * Updates specific Single day shift by ID.
 * @param shifts
 * @param id
 * @param shift
 * @returns
 */
export const updateSingleDayShift = (
  shifts: TSingleDayShift[],
  id: string,
  shift: Partial<TSingleDayShift>,
) => {
  const shiftIndex = shifts.findIndex(shift => shift.id === id)
  const newShifts = [...shifts]
  newShifts[shiftIndex] = {
    ...shifts[shiftIndex],
    ...shift,
  }
  return newShifts
}

/**
 * Updates a specific day in the days array and returns a new array. (multiday)
 * @param days Days array in formik
 * @param id Id of the specific day in the array
 * @param day Partial object from the new day
 */
export const updateSpecificDay = (
  days: TDay[],
  id: number,
  day: Partial<TDay>,
) => {
  const dayIndex = days.findIndex(day => day.id === id)
  const newDays = [...days]

  newDays[dayIndex] = {
    ...days[dayIndex],
    ...day,
  }

  return newDays
}

/**
 * Removes a Single day shift by ID.
 * @param shifts
 * @param id
 * @returns
 */
export const deleteSingleDayShift = (shifts: TSingleDayShift[], id: string) =>
  shifts.filter(shift => shift.id !== id)

export type TPaginatedSingleDayShifts = { [key: number]: TSingleDayShift[] }
/**
 * Hook that transforms a Single day shifts array into a paginated object, exposing it together with additional page fields.
 * Make sure to check if the paginatedShifts object is empty before passing it as a prop to the Table component.
 * It will be empty on the first render and the Table component will crash, receiving undefined value for data.
 * @param shifts Single Day Shifts
 * @returns
 */
export const usePaginatedSingleDayShifts = (shifts: TSingleDayShift[]) => {
  const [paginatedShifts, setPaginatedShifts] = useState<
    TPaginatedSingleDayShifts
  >({} as TPaginatedSingleDayShifts)
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const mapShiftsPerPage = useCallback(() => {
    let numberOfPages = 1
    if (shifts.length % 7) {
      numberOfPages = Math.floor(shifts.length / 7) + 1
    } else {
      numberOfPages = shifts.length / 7
    }

    setLastPage(numberOfPages)
    setCurrentPage(currentPage > numberOfPages ? numberOfPages : currentPage)
    let currentFillingPage = 1
    const preparedShifts: {
      [key: number]: TSingleDayShift[]
    } = {}
    for (let i = 0; i < shifts.length; i++) {
      if (i > 0 && i % 7 === 0 && currentFillingPage < numberOfPages) {
        currentFillingPage += 1
      }

      const currentFillingPageExists = !!preparedShifts[currentFillingPage]
      preparedShifts[currentFillingPage] = currentFillingPageExists
        ? [...preparedShifts[currentFillingPage], shifts[i]].sort(
            (a: TSingleDayShift, b: TSingleDayShift) => {
              return a.date_start.getTime() - b.date_start.getTime()
            },
          )
        : [shifts[i]].sort((a: TSingleDayShift, b: TSingleDayShift) => {
            return a.date_start.getTime() - b.date_start.getTime()
          })
    }
    setPaginatedShifts(preparedShifts)
  }, [currentPage, shifts])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  useEffect(() => {
    mapShiftsPerPage()
  }, [shifts, mapShiftsPerPage])

  return {
    paginatedShifts,
    currentPage,
    lastPage,
    handlePageChange,
  }
}

export const getUpdateShiftSuccessMessageTitleByStatus = (
  status?: SHIFT_STATUS,
) => {
  if (status === SHIFT_STATUS.DRAFT)
    return 'Your shift has been successfully saved as a draft. It will not be visible to Upshifters until you post it live.'

  return 'Shift successfully updated!'
}

/**
 * Function for checking whether auto accept certificates config should be shown or not for a shift
 * @param {IShift} shift
 * @returns {boolean}
 */
export const shouldRenderAAByCertificates = (shift?: IShift) => {
  const businessEntity = AuthService.getBusinessEntity()
  // if the business entity does not have auto_accept_by_certificates enabled
  // we won't show Auto Accept By Certificates fields
  if (!businessEntity?.auto_accept_by_certificates) return false
  // when this helper is used in edit shift view, we should rely on the shift's hide_auto_accept_certificates value
  if (shift) return !shift.hide_auto_accept_certificates
  // for other scenarios (create shift view), we should rely on business's hide_auto_accept_certificates value
  return !businessEntity.business_setting?.hide_auto_accept_certificates
}

/**
 * Function for checking whether a shift is a multiday shift
 * @param {IShift} shift
 * @returns {boolean}
 */
export const isMultidayShift = (shift: IShift) =>
  Number(shift.type) === SHIFT_TYPE.MULTIDAY

/**
 * Function for checking whether a shift is a singleday shift
 * @param {IShift} shift
 * @returns {boolean}
 */
export const isSingledayShift = (shift: IShift) =>
  Number(shift.type) === SHIFT_TYPE.SINGLEDAY

export const STARTING_DAY_ALERT_MESSAGE =
  "Choose the 'Next Hire Start Date' checkbox to set start dates for new hires as needed."

export const IN_PROGRESS_MULTIDAY_SHIFT_ALERT_MESSAGE =
  "You can only edit the 'New Hire Start Date' field for shifts that are in progress. Please contact our Customer Support team if you need further assistance."

export const IN_PROGRESS_MULTIDAY_SHIFT_NO_EDIT_ALERT_MESSAGE =
  'You can not edit shifts that are in progress. Please contact our Customer Support team if you need further assistance.'
/**
 * Function for checking whether the business requires next working day selection
 * i.e. Manual Posting setting is enabled for the business
 */
export const checkIfBusinessRequiresNextWorkingDaySelection = () => {
  const businessEntity = AuthService.getBusinessEntity()
  return (
    FEATURE_TOGGLE_MULTIDAY_REVAMP &&
    businessEntity?.business_setting?.requires_next_working_day_selection
  )
}

/**
 * Determines the minutes until shift starts
 * @param shift
 * @returns number
 */
export const getMinutesUntilShiftStarts = (shift: IShift) => {
  const shiftTimezone = new Date().toLocaleString('en-US', {
    timeZone: shift.property_timezone,
  })
  const minutesUntilShiftStarts = minutesDifferenceBetweenTwoDates(
    new Date(shift.time_start).getTime(),
    new Date(shiftTimezone).getTime(),
  )
  return minutesUntilShiftStarts
}

/**
 * Determines whether the Invite Upshifters button is disabled or not for a shift
 * @param shift
 * @returns boolean
 */
export const isInviteUpshiftersBtnDisabled = (shift: IShift) => {
  const minutesUntilShiftStarts = getMinutesUntilShiftStarts(shift)
  const isShiftLocked = shift.is_shift_locked ?? false

  return (
    minutesUntilShiftStarts < 30 ||
    shift.status === SHIFT_STATUS.DRAFT ||
    isShiftLocked
  )
}

/**
 * Determines the tooltip message for Invite Upshifters button, based on shift status and start time
 * @param shift
 * @returns string - The corresponding tooltip message
 */
export const getInviteUpshiftersTooltipText = (shift: IShift) => {
  if (shift.status === SHIFT_STATUS.DRAFT)
    return 'You cannot invite Upshifters on draft shifts'

  const minutesUntilShiftStarts = getMinutesUntilShiftStarts(shift)

  if (minutesUntilShiftStarts <= 0)
    return 'You cannot invite Upshifters on already started or finished shifts'

  if (minutesUntilShiftStarts < 30)
    return 'You cannot invite new Upshifters to a shift within 30 minutes of the start time'

  if (shift.is_shift_locked)
    return 'This shift is locked, so you can’t invite Upshifters right now. Unlock the shift to continue.'

  return 'Invite Upshifters'
}

/**
 * Returns whether Last Minute Candidate Additions are restricted for the shift
 * @param shift
 * @returns boolean
 */
export const areLastMinuteAdditionsRestricted = (shift: IShift) => {
  const minutesUntilShiftStarts = getMinutesUntilShiftStarts(shift)
  const businessEntity = AuthService.getBusinessEntity()
  const restrictLastMinuteAdditions =
    businessEntity?.vendor_management_system?.restrict_last_minute_additions ??
    false
  const shiftFreezeTimeHours =
    businessEntity?.vendor_management_system?.shift_freeze_hours || 3

  return (
    FEATURE_TOGGLE_RESTRICT_LAST_MINUTE_CANDIDATE_ADDITIONS_IN_THE_VMS &&
    restrictLastMinuteAdditions &&
    minutesUntilShiftStarts < shiftFreezeTimeHours * 60
  )
}

/**
 * Returns whether Invite Upshifters can be accessed for the shift
 * @param shift
 * @returns boolean
 */
export const canAccessInviteUpshifters = (shift: IShift) => {
  const businessEntity = AuthService.getBusinessEntity()
  const canRequestUpshifters =
    businessEntity?.business_setting?.can_request_upshifters ?? false

  return (
    FEATURE_TOGGLE_INVITE_UPSHIFTERS_REVAMP &&
    canRequestUpshifters &&
    !areLastMinuteAdditionsRestricted(shift) &&
    shift.status === SHIFT_STATUS.POSTED
  )
}

/**
 * Checks whether Selective Claim Mode (Selective Auto Accept/SAA) is enabled for a shift.
 * We consider Selective Claim Mode enabled for a shift, when one of the following settings are enabled for a shift:
 * "Auto-Accept By Favorited", "Auto-Accept by Worked before", "Auto-Accept by Certificates", "Auto-Accept by Onboarded"
 * @param shift
 * @returns boolean
 */
export const hasShiftSelectiveClaimModeEnabled = (
  shift: IShift | IModalEditShiftValues | IModalDuplicateShiftValues,
) => {
  if (!shift) return false
  // In order to enable Selective Claim Mode for a shift, we need to enable Claim Mode (Auto Accept/AUTO) for the shift first.
  // We consider Claim Mode enabled for a shift, when "Auto Accept" setting is enabled for a shift.
  if (!shift.auto_accept) return false
  return (
    (shift.auto_accept_by_certificates &&
      !shift.hide_auto_accept_certificates) ||
    shift.auto_accept_by_onboarded ||
    shift.auto_accept_by_rosters
  )
}

type TGetToHireShiftWarningModalConfigProps = {
  onConfirm: () => void
  closeModal: () => void
}

/**
 * Returns modal config for the warning modal shown when editing start date of a multi-day to hire shift
 */
export const getToHireShiftWarningModalConfig = ({
  onConfirm,
  closeModal,
}: TGetToHireShiftWarningModalConfigProps): TGenericModalConfig => ({
  fullWidth: true,
  maxWidth: 'xs',
  title: 'Attention required',
  content:
    'You will lose all changes made to the days in the current range. Are you sure you want to continue?',
  footer: (
    <>
      <Button type="dark" isFilledOutlined onClick={closeModal}>
        Cancel
      </Button>
      <Button
        onClick={() => {
          onConfirm()
          closeModal()
        }}
      >
        Confirm
      </Button>
    </>
  ),
})

/**
 * Function that checks if the shift is over 8 hours long and the pay rate is less than the exempt rate.
 * If the conditions are met, a warning alert should be shown.
 */
export const shouldShowOTPolicyWarning = (
  values: TEditDuplicateShiftValues,
  payRateLimit: number,
  isEdit?: boolean,
) => {
  // Safeguard for post from template, returns a values object with missing parameters
  if (values.type === undefined) {
    return false
  }

  const checkOvertimeWarning = (
    payValue: string | number,
    hoursDuration: number,
  ): boolean => {
    return Number(payValue) < payRateLimit && hoursDuration > 8
  }

  if (values.type === SHIFT_TYPE.SINGLEDAY) {
    if (isEdit) {
      const hoursDuration = calculateShiftDurationHours(
        values.date_start,
        values.hour_start,
        values.hour_end,
      )
      return checkOvertimeWarning(values.pay_value, hoursDuration)
    }

    const singleDayShifts = (values as IModalPostFromTemplateShiftValues)
      .single_day_shifts
    return singleDayShifts.some(day => {
      const hoursDuration = calculateShiftDurationHours(
        day.id,
        day.hour_start,
        day.hour_end,
      )
      return checkOvertimeWarning(day.pay_value, hoursDuration)
    })
  }

  // Multiday
  const days = values.to_hire ? values.individual_days : values.days
  return days.some(day => {
    const hoursDuration = calculateShiftDurationHours(
      day.date_start,
      day.hour_start,
      day.hour_end,
    )
    return checkOvertimeWarning(day.pay_value, hoursDuration)
  })
}

export const useOTPolicyWarning = (
  values: TEditDuplicateShiftValues,
  isEdit?: boolean,
) => {
  const properties = useSelector(selectProperties)
  const selectedProperty = properties.find(
    property => property.id === values.property_id,
  )
  const payRateLimit = selectedProperty?.active_overtime_policy?.pay_rate_limit

  return {
    showOTPolicyWarning: payRateLimit
      ? shouldShowOTPolicyWarning(values, payRateLimit, isEdit)
      : false,
    payRateLimit: payRateLimit ?? 0,
  }
}

/**
 * shift pay_value should be updated with min_wage when business cannot edit pay_value and the pay_value is less than min_wage
 * This function is used for covering the following edge case:
 * "When a business user wants to COPY some older shift where the pay_rate value is lower than the min_wage, we should display the pay_rate value based on the current min_wage of the property (location)."
 * RMAP-352 Prevent Sodexo-related clients from editing pay rates
 * @param shiftPayValue pay_value of the shift
 * @param propertyMinWage min_wave of the shift property
 * @returns
 */
export const shouldUpdatePayValueBasedOnMinWage = (
  shiftPayValue: string,
  propertyMinWage: string,
) => {
  return (
    hasBusinessPayRateUpdateRestriction() &&
    Number(shiftPayValue) < Number(propertyMinWage)
  )
}

/**
 * Function that checks if the shift is over 8 hours long and the pay rate is less than the exempt rate.
 * If the conditions are met, a warning alert should be shown.
 */
export const shouldShowOTPolicyWarningV2 = (
  values: IScheduleSectionFormValues,
  payRateLimit: number,
  isEdit?: boolean,
) => {
  const checkOvertimeWarning = (
    payValue: string | number,
    hoursDuration: number,
  ): boolean => {
    return Number(payValue) < payRateLimit && hoursDuration > 8
  }

  if (values.type === SHIFT_TYPE.SINGLEDAY) {
    if (isEdit) {
      const hoursDuration = calculateShiftDurationHours(
        values.date_start,
        values.hour_start,
        values.hour_end,
      )
      return checkOvertimeWarning(values.pay_value, hoursDuration)
    }

    const singleDayShifts = values.single_day_shifts
    return singleDayShifts.some(day => {
      const hoursDuration = calculateShiftDurationHours(
        day.id,
        day.hour_start,
        day.hour_end,
      )
      return checkOvertimeWarning(day.pay_value, hoursDuration)
    })
  }

  // Multiday
  const days = values.days
  return days.some(day => {
    const hoursDuration = calculateShiftDurationHours(
      day.date_start,
      day.hour_start,
      day.hour_end,
    )
    return checkOvertimeWarning(day.pay_value, hoursDuration)
  })
}
